import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

/** Staggered entrance wrapper — `index` drives a 60ms cascade. */
export function Reveal({
  index = 0,
  className,
  children,
}: {
  index?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE_OUT, delay: 0.06 + index * 0.06 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/** Soft rounded surface used for every dashboard block. */
export function SectionCard({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        "border-border/60 bg-card rounded-[1.25rem] border p-4 shadow-[var(--shadow-card)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Section title with an optional trailing action (e.g. "View all"). */
export function SectionHeader({
  title,
  action,
  onAction,
  level = 2,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
  /** `1` when this header is the page title rather than a section within one. */
  level?: 1 | 2;
}) {
  const Heading = level === 1 ? "h1" : "h2";
  return (
    <div className="mb-3 flex items-center justify-between">
      <Heading className="font-display text-base font-semibold">{title}</Heading>
      {action && onAction && (
        <button
          type="button"
          onClick={onAction}
          /* Several sections share the same action word ("View all"), so the
             bare label is ambiguous once you are tabbing rather than looking. */
          aria-label={`${action} — ${title}`}
          className="text-primary text-xs font-semibold transition-opacity hover:opacity-70"
        >
          {action}
        </button>
      )}
    </div>
  );
}
